import type { ComponentProps } from "react"
import { ArrowDownIcon } from "@phosphor-icons/react"
import {
  MessageScroller as MessageScrollerPrimitive,
  useMessageScroller,
  useMessageScrollerScrollable,
} from "@shadcn/react/message-scroller"

import { Button } from "~/components/design-system/components"
import { cn } from "~/utils/helpers"

/**
 * Chat transcript scroll container: anchors new turns near the top, follows streamed replies
 * only while the reader is at the live edge, and reopens saved threads at the last turn. Port of
 * shadcn/ui `message-scroller` (radix-nova) onto Hakio tokens.
 * {@link ../design-system/intents.ts MESSAGE_SCROLLER_INTENT}
 */
function MessageScroller({
  className,
  ...props
}: ComponentProps<typeof MessageScrollerPrimitive.Root>) {
  return (
    <MessageScrollerPrimitive.Root
      data-slot="message-scroller"
      className={cn(
        "group/message-scroller relative flex size-full min-h-0 flex-col overflow-hidden",
        className,
      )}
      {...props}
    />
  )
}

function MessageScrollerViewport({
  className,
  ...props
}: ComponentProps<typeof MessageScrollerPrimitive.Viewport>) {
  return (
    <MessageScrollerPrimitive.Viewport
      data-slot="message-scroller-viewport"
      className={cn(
        "size-full min-h-0 min-w-0 overflow-y-auto overscroll-contain contain-content [scrollbar-gutter:stable] data-pending-scroll:invisible",
        className,
      )}
      {...props}
    />
  )
}

function MessageScrollerContent({
  className,
  ...props
}: ComponentProps<typeof MessageScrollerPrimitive.Content>) {
  return (
    <MessageScrollerPrimitive.Content
      data-slot="message-scroller-content"
      className={cn("flex h-max min-h-full flex-col gap-2", className)}
      {...props}
    />
  )
}

function MessageScrollerItem({
  className,
  ...props
}: ComponentProps<typeof MessageScrollerPrimitive.Item>) {
  return (
    <MessageScrollerPrimitive.Item
      data-slot="message-scroller-item"
      // No content-visibility: Haki turns hold tall tables and charts, so estimated row heights
      // throw off anchoring, and paint containment clips bubble shadows.
      className={cn("min-w-0 shrink-0", className)}
      {...props}
    />
  )
}

function MessageScrollerButton({
  direction = "end",
  className,
  children,
  render,
  ...props
}: ComponentProps<typeof MessageScrollerPrimitive.Button>) {
  return (
    <MessageScrollerPrimitive.Button
      data-slot="message-scroller-button"
      data-direction={direction}
      direction={direction}
      // Opaque bg-base: it floats over text, and the neutral button fill is 4% white in dark mode.
      // pr-2.5 offsets the arrow glyph's built-in inset so the label doesn't look cramped.
      className={cn(
        "absolute z-10 left-1/2 -translate-x-1/2 pr-2.5 bg-base hover:bg-base-hover shadow-flyout transition-[translate,scale,opacity] duration-200 data-[active=false]:pointer-events-none data-[active=false]:scale-95 data-[active=false]:opacity-0 data-[active=true]:scale-100 data-[active=true]:opacity-100 data-[direction=end]:bottom-2 data-[direction=start]:top-2 data-[direction=start]:[&_svg]:rotate-180 motion-reduce:transition-none",
        className,
      )}
      render={render ?? <Button variant="neutral" size="sm" />}
      {...props}
    >
      {children ?? (
        <>
          <ArrowDownIcon />
          {direction === "end" ? "Latest" : "Earliest"}
        </>
      )}
    </MessageScrollerPrimitive.Button>
  )
}

const MessageScrollerProvider = MessageScrollerPrimitive.Provider

export {
  MessageScroller,
  MessageScrollerButton,
  MessageScrollerContent,
  MessageScrollerItem,
  MessageScrollerProvider,
  MessageScrollerViewport,
  useMessageScroller,
  useMessageScrollerScrollable,
}
